import type { U3129HonorariumItem } from '../U3129Service';
import {
  type U3129HonorariumProjectGroup,
  u3129HonorariumProjectHeaderLabels,
} from './u3129GroupHonorariumForDisplay';

export type U3129HonorariumGroupCheckState = 'none' | 'partial' | 'all';

export type U3129HonorariumHeaderCheckState = 'none' | 'partial' | 'all';

export type U3129HonorariumDeleteConfirmKind = 'lines' | 'projects' | 'mixed' | 'subproject';

export type U3129HonorariumDeleteProjectPreview = {
  projectKey: string;
  projectLabel: string;
  clientLabel: string;
  hours: number;
  lineCount: number;
};

export type U3129HonorariumDeleteConfirmPreview = {
  kind: U3129HonorariumDeleteConfirmKind;
  checkedIndices: number[];
  fullProjects: U3129HonorariumDeleteProjectPreview[];
  looseLineCount: number;
};

function isRowChecked(row: U3129HonorariumItem): boolean {
  return (row as unknown as Record<string, unknown>).checked === true;
}

export function u3129HonorariumGroupCheckState(
  lineIndices: readonly number[],
  rows: readonly U3129HonorariumItem[],
): U3129HonorariumGroupCheckState {
  if (lineIndices.length === 0) {
    return 'none';
  }
  let checkedCount = 0;
  for (const index of lineIndices) {
    const row = rows[index];
    if (row && isRowChecked(row)) {
      checkedCount += 1;
    }
  }
  if (checkedCount === 0) {
    return 'none';
  }
  if (checkedCount === lineIndices.length) {
    return 'all';
  }
  return 'partial';
}

export function u3129HonorariumHeaderCheckState(
  projectGroups: readonly U3129HonorariumProjectGroup[],
  rows: readonly U3129HonorariumItem[],
): U3129HonorariumHeaderCheckState {
  const allIndices = projectGroups.flatMap((g) => g.allLines.map((l) => l.index));
  if (allIndices.length === 0) {
    return 'none';
  }
  return u3129HonorariumGroupCheckState(allIndices, rows);
}

export function u3129HonorariumIndicesForGroupToggle(
  lineIndices: readonly number[],
  rows: readonly U3129HonorariumItem[],
): number[] {
  const state = u3129HonorariumGroupCheckState(lineIndices, rows);
  const shouldCheck = state !== 'all';
  return lineIndices.filter((index) => {
    const row = rows[index];
    if (!row) {
      return false;
    }
    return isRowChecked(row) !== shouldCheck;
  });
}

export function u3129HonorariumIndicesForSelectAllToggle(
  projectGroups: readonly U3129HonorariumProjectGroup[],
  rows: readonly U3129HonorariumItem[],
): number[] {
  const allIndices = projectGroups.flatMap((g) => g.allLines.map((l) => l.index));
  return u3129HonorariumIndicesForGroupToggle(allIndices, rows);
}

export function u3129HonorariumDeleteConfirmPreview(
  projectGroups: readonly U3129HonorariumProjectGroup[],
  rows: readonly U3129HonorariumItem[],
): U3129HonorariumDeleteConfirmPreview | null {
  const checkedIndices = rows
    .map((r, i) => (isRowChecked(r) ? i : -1))
    .filter((i) => i >= 0);
  if (checkedIndices.length === 0) {
    return null;
  }
  const checkedSet = new Set(checkedIndices);
  const fullProjects: U3129HonorariumDeleteProjectPreview[] = [];
  const coveredByFullProject = new Set<number>();

  for (const group of projectGroups) {
    const indices = group.allLines.map((l) => l.index);
    if (indices.length === 0) {
      continue;
    }
    const allChecked = indices.every((idx) => checkedSet.has(idx));
    if (!allChecked) {
      continue;
    }
    const { projectLabel, clientLabel } = u3129HonorariumProjectHeaderLabels(group.sampleRow);
    fullProjects.push({
      projectKey: group.projectKey,
      projectLabel,
      clientLabel,
      hours: group.totals.grandTotal,
      lineCount: indices.length,
    });
    for (const idx of indices) {
      coveredByFullProject.add(idx);
    }
  }

  const looseLineCount = checkedIndices.filter((idx) => !coveredByFullProject.has(idx)).length;

  let kind: U3129HonorariumDeleteConfirmKind;
  if (fullProjects.length > 0 && looseLineCount === 0) {
    kind = 'projects';
  } else if (fullProjects.length > 0 && looseLineCount > 0) {
    kind = 'mixed';
  } else {
    kind = 'lines';
  }

  return {
    kind,
    checkedIndices,
    fullProjects,
    looseLineCount,
  };
}
